import { useCallback } from 'react';
import { useAccount } from 'wagmi';
import { maxUint256 } from 'viem';
import { scroogeAbi, SCROOGE_ADDRESS } from '../../lib/contract';
import { USDT_ADDRESS, erc20Abi } from '../../lib/addresses';
import { useTxLifecycle } from './useTxLifecycle';
import { mockStore } from '../../lib/mockStore';
import { FALLBACK_PLANS } from './useAllPlans';

const INVALIDATE_ALL = [['readContract'], ['readContracts'], ['balance']];

export function useApproveUsdt() {
  const tx = useTxLifecycle({ invalidateKeys: INVALIDATE_ALL });
  const { address } = useAccount();
  const approve = useCallback(
    (amount) =>
      tx.send({
        abi: erc20Abi,
        address: USDT_ADDRESS,
        functionName: 'approve',
        args: [SCROOGE_ADDRESS, amount ?? maxUint256],
        label: 'Approve USDT',
        successMessage: 'USDT approved',
        mockAction: () => mockStore.approve(address, amount ?? maxUint256),
      }),
    [tx, address],
  );
  return { ...tx, approve };
}

export function useDeposit() {
  const tx = useTxLifecycle({ invalidateKeys: INVALIDATE_ALL });
  const { address } = useAccount();

  const deposit = useCallback(
    ({ amount, planId, referrer }) => {
      const ref = referrer && referrer !== address ? referrer : '0x0000000000000000000000000000000000000000';
      return tx.send({
        abi: scroogeAbi,
        address: SCROOGE_ADDRESS,
        functionName: 'deposit',
        args: [amount, planId, ref],
        label: 'Deposit',
        successMessage: 'Deposit created',
        mockAction: () =>
          mockStore.deposit(address, { amount, planId, referrer: ref, plans: FALLBACK_PLANS }),
      });
    },
    [tx, address],
  );
  return { ...tx, deposit };
}

export function useClaim() {
  const tx = useTxLifecycle({ invalidateKeys: INVALIDATE_ALL });
  const { address } = useAccount();
  const claim = useCallback(
    (depositIdx) =>
      tx.send({
        abi: scroogeAbi,
        address: SCROOGE_ADDRESS,
        functionName: 'claim',
        args: [BigInt(depositIdx)],
        label: 'Claim profit',
        successMessage: 'Profit claimed',
        mockAction: () => mockStore.claim(address, depositIdx, FALLBACK_PLANS),
      }),
    [tx, address],
  );
  return { ...tx, claim };
}

export function useReinvest() {
  const tx = useTxLifecycle({ invalidateKeys: INVALIDATE_ALL });
  const { address } = useAccount();
  const reinvest = useCallback(
    (srcIdx, targetPlanId) =>
      tx.send({
        abi: scroogeAbi,
        address: SCROOGE_ADDRESS,
        functionName: 'reinvest',
        args: [BigInt(srcIdx), targetPlanId],
        label: 'Reinvest',
        successMessage: 'Profit reinvested',
        mockAction: () => mockStore.reinvest(address, srcIdx, targetPlanId, FALLBACK_PLANS),
      }),
    [tx, address],
  );
  return { ...tx, reinvest };
}

// Reinvest на matured позиции: principal + финальный yield → новая позиция.
// Контракт не поддерживает это атомарно (нет функции reinvestFull), поэтому на реальной сети
// это последовательность: withdraw() → approve() если нужно → deposit(principal+net, targetPlan).
// В mock-режиме всё одной операцией через mockStore.reinvestFull.
// TODO(real): после деплоя контракта реализовать полную цепочку из двух подписей.
export function useReinvestFull() {
  const tx = useTxLifecycle({ invalidateKeys: INVALIDATE_ALL });
  const { address } = useAccount();
  const reinvestFull = useCallback(
    (srcIdx, targetPlanId) =>
      tx.send({
        abi: scroogeAbi,
        address: SCROOGE_ADDRESS,
        functionName: 'withdraw', // placeholder — не вызовется в mock-режиме
        args: [BigInt(srcIdx)],
        label: 'Full reinvest',
        successMessage: 'Position rolled into new strategy',
        mockAction: () => mockStore.reinvestFull(address, srcIdx, targetPlanId, FALLBACK_PLANS),
      }),
    [tx, address],
  );
  return { ...tx, reinvestFull };
}

export function useWithdraw() {
  const tx = useTxLifecycle({ invalidateKeys: INVALIDATE_ALL });
  const { address } = useAccount();
  const withdraw = useCallback(
    (depositIdx) =>
      tx.send({
        abi: scroogeAbi,
        address: SCROOGE_ADDRESS,
        functionName: 'withdraw',
        args: [BigInt(depositIdx)],
        label: 'Claim principal + yield',
        successMessage: 'Principal + yield sent to wallet',
        mockAction: () => mockStore.withdraw(address, depositIdx, FALLBACK_PLANS),
      }),
    [tx, address],
  );
  return { ...tx, withdraw };
}

export function useClaimReferral() {
  const tx = useTxLifecycle({ invalidateKeys: INVALIDATE_ALL });
  const { address } = useAccount();
  const claim = useCallback(
    () =>
      tx.send({
        abi: scroogeAbi,
        address: SCROOGE_ADDRESS,
        functionName: 'claimReferral',
        args: [],
        label: 'Claim referral',
        successMessage: 'Referral bonus claimed',
        mockAction: () => mockStore.claimReferral(address),
      }),
    [tx, address],
  );
  return { ...tx, claim };
}

export function useUpdateSponsor() {
  const tx = useTxLifecycle({ invalidateKeys: INVALIDATE_ALL });
  const { address } = useAccount();
  const update = useCallback(
    (newSponsor) =>
      tx.send({
        abi: scroogeAbi,
        address: SCROOGE_ADDRESS,
        functionName: 'updateSponsor',
        args: [newSponsor],
        label: 'Update sponsor',
        successMessage: 'Sponsor updated',
        mockAction: () => mockStore.updateSponsor(address, newSponsor),
      }),
    [tx, address],
  );
  return { ...tx, update };
}
